import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { toast } from 'sonner'
import { Users, Activity, Calendar, TrendingUp, Lock, Eye, EyeOff, Loader2, Receipt, Truck, Sun, Trash2 } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL

// Protected admin emails that cannot be deleted
const PROTECTED_ADMIN_EMAILS = ['twilatee@gmail.com']

export default function Admin() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [adminKey, setAdminKey] = useState('')
  const [deleteUser, setDeleteUser] = useState(null) // User to delete
  const [deleting, setDeleting] = useState(false)

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      const response = await fetch(`${API_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      })
      
      if (response.ok) {
        setAuthenticated(true)
        setAdminKey(password)
        toast.success('Admin access granted')
        fetchUsers(password)
      } else {
        toast.error('Invalid admin password')
      }
    } catch (err) {
      toast.error('Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async (key) => {
    setLoading(true)
    try {
      const response = await fetch(`${API_URL}/api/admin/users?admin_key=${encodeURIComponent(key)}`)
      if (response.ok) {
        const result = await response.json()
        setData(result)
      } else {
        toast.error('Failed to fetch users')
        setAuthenticated(false)
      }
    } catch (err) {
      toast.error('Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async () => {
    if (!deleteUser) return
    
    // Check if trying to delete protected admin account (client-side check)
    if (PROTECTED_ADMIN_EMAILS.includes(deleteUser.email.toLowerCase())) {
      toast.error('Cannot delete admin account. This account is protected.')
      setDeleteUser(null)
      return
    }
    
    setDeleting(true)
    try {
      const response = await fetch(`${API_URL}/api/admin/delete-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: deleteUser.email, admin_key: adminKey })
      })
      const result = await response.json()
      
      if (response.ok) {
        toast.success(result.message || `Account ${deleteUser.email} deleted successfully`)
        setDeleteUser(null)
        fetchUsers(adminKey) // Auto-refresh the list
      } else {
        toast.error(result.detail || 'Failed to delete user')
      }
    } catch (err) {
      toast.error('Failed to delete user')
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatShortDate = (dateStr) => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: '2-digit'
    })
  }

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-secondary/20">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-['Barlow_Condensed']">ADMIN ACCESS</CardTitle>
            <p className="text-muted-foreground text-sm">Enter admin password to continue</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label>Admin Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="h-12 pr-10"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3.5 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full h-12" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Access Dashboard
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Loading state
  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-background p-4 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Barlow_Condensed']">ADMIN DASHBOARD</h1>
            <p className="text-muted-foreground">User management & analytics</p>
          </div>
          <Button variant="outline" onClick={() => fetchUsers(adminKey)} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>

        {/* Summary Cards */}
        {data?.summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{data.summary.total_users}</p>
                    <p className="text-xs text-muted-foreground">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{data.summary.active_last_7_days}</p>
                    <p className="text-xs text-muted-foreground">Active (7 days)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{data.summary.signups_last_7_days}</p>
                    <p className="text-xs text-muted-foreground">New (7 days)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{data.summary.signups_last_30_days}</p>
                    <p className="text-xs text-muted-foreground">New (30 days)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Table - Desktop */}
        <Card className="hidden lg:block">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed']">ALL USERS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">Email</th>
                    <th className="text-left p-3 font-semibold">Name</th>
                    <th className="text-left p-3 font-semibold">Role</th>
                    <th className="text-left p-3 font-semibold">Sign-up</th>
                    <th className="text-left p-3 font-semibold">Last Login</th>
                    <th className="text-center p-3 font-semibold">Expenses</th>
                    <th className="text-center p-3 font-semibold">Loads</th>
                    <th className="text-center p-3 font-semibold">Per Diem</th>
                    <th className="text-center p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.users?.map((user, index) => (
                    <tr key={index} className="border-b hover:bg-secondary/50">
                      <td className="p-3 font-mono text-xs">{user.email}</td>
                      <td className="p-3">{user.name}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.role === 'driver' ? 'bg-blue-500/20 text-blue-500' : 'bg-green-500/20 text-green-500'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="p-3 text-muted-foreground">{formatShortDate(user.created_at)}</td>
                      <td className="p-3 text-muted-foreground">{formatShortDate(user.last_login)}</td>
                      <td className="p-3 text-center">
                        <span className={user.activity.expenses > 0 ? 'text-yellow-500 font-semibold' : 'text-muted-foreground'}>
                          {user.activity.expenses}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={user.activity.loads > 0 ? 'text-blue-500 font-semibold' : 'text-muted-foreground'}>
                          {user.activity.loads}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={user.activity.per_diem_days > 0 ? 'text-purple-500 font-semibold' : 'text-muted-foreground'}>
                          {user.activity.per_diem_days}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteUser(user)}
                          disabled={PROTECTED_ADMIN_EMAILS.includes(user.email.toLowerCase())}
                          className={`h-8 w-8 ${
                            PROTECTED_ADMIN_EMAILS.includes(user.email.toLowerCase())
                              ? 'text-muted-foreground cursor-not-allowed opacity-30'
                              : 'text-destructive hover:text-destructive hover:bg-destructive/10'
                          }`}
                          title={PROTECTED_ADMIN_EMAILS.includes(user.email.toLowerCase()) ? 'Admin account protected' : 'Delete user'}
                          data-testid={`delete-user-${user.email}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Users Cards - Mobile */}
        <div className="lg:hidden space-y-3">
          <h2 className="text-xl font-bold font-['Barlow_Condensed']">ALL USERS</h2>
          {data?.users?.map((user, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{user.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.role === 'driver' ? 'bg-blue-500/20 text-blue-500' : 'bg-green-500/20 text-green-500'
                      }`}>
                        {user.role}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteUser(user)}
                        disabled={PROTECTED_ADMIN_EMAILS.includes(user.email.toLowerCase())}
                        className={`h-8 w-8 ${
                          PROTECTED_ADMIN_EMAILS.includes(user.email.toLowerCase())
                            ? 'text-muted-foreground cursor-not-allowed opacity-30'
                            : 'text-destructive hover:text-destructive hover:bg-destructive/10'
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Sign-up:</span>
                      <span className="ml-1">{formatShortDate(user.created_at)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last login:</span>
                      <span className="ml-1">{formatShortDate(user.last_login)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 pt-2 border-t">
                    <div className="flex items-center gap-1">
                      <Receipt className="w-4 h-4 text-yellow-500" />
                      <span className={user.activity.expenses > 0 ? 'font-semibold' : 'text-muted-foreground'}>
                        {user.activity.expenses}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Truck className="w-4 h-4 text-blue-500" />
                      <span className={user.activity.loads > 0 ? 'font-semibold' : 'text-muted-foreground'}>
                        {user.activity.loads}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Sun className="w-4 h-4 text-purple-500" />
                      <span className={user.activity.per_diem_days > 0 ? 'font-semibold' : 'text-muted-foreground'}>
                        {user.activity.per_diem_days}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground py-4">
          <p>Admin Dashboard • DriverPNL</p>
          <p>Data refreshed: {new Date().toLocaleString()}</p>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Account?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to delete this account?</p>
              <div className="bg-secondary p-3 rounded-lg mt-2">
                <p className="font-semibold">{deleteUser?.name}</p>
                <p className="text-sm font-mono">{deleteUser?.email}</p>
                {deleteUser?.activity?.total > 0 && (
                  <p className="text-xs text-destructive mt-1">
                    Warning: This user has {deleteUser.activity.total} data entries that will also be deleted.
                  </p>
                )}
              </div>
              <p className="text-sm text-destructive font-medium mt-2">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
